<template>
  <v-card class="card-container">
    <div class="main-toolbar">
      <div class="icon-back">
        <v-icon color="white" large>mdi-water-outline</v-icon>
      </div>

      <span class="main-toolbar-title">{{ $t('Controle de Consumo') }}</span>

      <v-spacer></v-spacer>
    </div>

    <div class="mt-4">
      <v-tabs color="secondary" v-model="tab">
        <v-tab class="rounded-lg rounded-b-0">{{ $t('Indicadores') }}</v-tab>
        <v-tab
          class="rounded-lg rounded-b-0"
          v-if="institution.userType < 4 || institution.userType == 8"
          >Tipos de Consumo</v-tab
        >
        <v-tab
          class="rounded-lg rounded-b-0"
          v-if="institution.userType < 4 || institution.userType == 8"
          >{{ $t('Leituras') }}</v-tab
        >
      </v-tabs>
    </div>

    <v-tabs-items v-model="tab">
      <!-- ═══ Tab: Indicadores ═══ -->
      <v-tab-item>
        <v-tabs grow color="secondary" v-model="indicatorTab">
          <v-tab class="rounded-lg rounded-b-0">{{ $t('Indicadores') }}</v-tab>
          <v-tab class="rounded-lg rounded-b-0">{{ $t('Dados') }}</v-tab>
        </v-tabs>

        <!-- Shared filters -->
        <div class="filter-row mt-4">
          <div class="filter-col">
            <span class="filter-label">{{ $t('Tipo de consumo') }}</span>
            <v-autocomplete
              v-model="chartFilter.consumptionTypeId"
              placeholder="Todos"
              hide-details
              clearable
              append-icon="mdi-chevron-down"
              outlined
              dense
              :items="allTypes"
              item-text="name"
              item-value="id"
              class="primary-input filter-input"
              @change="onTypeChange"
            ></v-autocomplete>
          </div>

          <div class="filter-col">
            <span class="filter-label">{{ $t('Ponto de leitura') }}</span>
            <v-autocomplete
              v-model="chartFilter.consumptionPointId"
              placeholder="Todos"
              hide-details
              clearable
              append-icon="mdi-chevron-down"
              outlined
              dense
              :items="chartPoints"
              item-text="name"
              item-value="id"
              class="primary-input filter-input"
            ></v-autocomplete>
          </div>

          <div class="filter-col">
            <span class="filter-label">{{ $t('Unidade') }}</span>
            <v-autocomplete
              v-model="chartFilter.unidadeId"
              placeholder="Todas"
              hide-details
              clearable
              append-icon="mdi-chevron-down"
              outlined
              dense
              :items="unidades"
              item-text="name"
              item-value="id"
              class="primary-input filter-input"
            ></v-autocomplete>
          </div>

          <div class="filter-col filter-col--period">
            <span class="filter-label">{{ $t('Periodo') }}</span>
            <div class="d-flex align-center" style="gap: 8px">
              <v-menu
                ref="startMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    class="primary-button mr-0 filter-btn"
                    v-bind="attrs"
                    v-on="on"
                  >{{ formatMonth(chartFilter.startDate) }}</v-btn>
                </template>
                <v-date-picker
                  v-model="chartFilter.startDate"
                  type="month"
                  :locale="$i18n.locale"
                  no-title
                  scrollable
                  @change="$refs.startMenu.save(chartFilter.startDate)"
                ></v-date-picker>
              </v-menu>
              <span>{{ $t('até') }}</span>
              <v-menu
                ref="endMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    class="primary-button mr-0 filter-btn"
                    v-bind="attrs"
                    v-on="on"
                  >{{ formatMonth(chartFilter.endDate) }}</v-btn>
                </template>
                <v-date-picker
                  v-model="chartFilter.endDate"
                  type="month"
                  :locale="$i18n.locale"
                  no-title
                  scrollable
                  @change="$refs.endMenu.save(chartFilter.endDate)"
                ></v-date-picker>
              </v-menu>
              <v-btn class="secondary-button mr-0 filter-btn" depressed @click="applyChartFilter" :loading="chartLoading">
                {{ $t('Filtrar') }}
              </v-btn>
              <v-btn
                v-if="indicatorTab === 0"
                class="terciary-button mr-0 filter-btn"
                dark
                depressed
                :loading="excelLoading"
                :disabled="chartLoading || !hasChartData"
                @click="requestExcel"
              >
                {{ $t('Exportar excel') }}
              </v-btn>
            </div>
          </div>
        </div>

        <v-tabs-items v-model="indicatorTab">
          <!-- Sub-tab: Indicadores (charts) -->
          <v-tab-item>
            <div class="dark-panel mt-4">
              <div v-if="chartLoading" class="d-flex justify-center align-center" style="height: 400px">
                <v-progress-circular indeterminate color="#bbcf20" size="48"></v-progress-circular>
              </div>

              <div v-else-if="!hasChartData" class="text-center pa-8">
                <v-icon dark size="48" class="mb-3" style="opacity: 0.4">mdi-chart-bar</v-icon>
                <div class="white--text" style="opacity: 0.6">
                  {{ $t('Nenhum dado encontrado. Selecione os filtros e clique em "Filtrar".') }}
                </div>
              </div>

              <template v-else>
                <!-- KPI Cards -->
                <v-row class="ma-0 mb-4" no-gutters>
                  <v-col v-for="(kpi, ki) in kpiCards" :key="ki" cols="6" sm="3" class="pa-2">
                    <div class="kpi-card">
                      <div class="kpi-value" :style="{ color: kpi.color }">{{ kpi.value }}</div>
                      <div class="kpi-label">{{ kpi.label }}</div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Charts Grid -->
                <v-row class="ma-0">
                  <v-col cols="12" md="6" class="pa-2">
                    <div class="chart-card">
                      <h3 class="chart-title">{{ $t('Custo mês a mês') }}</h3>
                      <v-chart class="chart" :option="costByMonthOption" autoresize />
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" class="pa-2">
                    <div class="chart-card">
                      <h3 class="chart-title">{{ $t('Consumo mês a mês') }}</h3>
                      <v-chart class="chart" :option="consumptionByMonthOption" autoresize />
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" class="pa-2">
                    <div class="chart-card">
                      <h3 class="chart-title">{{ $t('Custo por ponto de leitura') }}</h3>
                      <v-chart class="chart chart--tall" :option="costByPointOption" autoresize />
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" class="pa-2">
                    <div class="chart-card">
                      <h3 class="chart-title">{{ $t('Consumo por ponto de leitura') }}</h3>
                      <v-chart class="chart chart--tall" :option="consumptionByPointOption" autoresize />
                    </div>
                  </v-col>
                </v-row>
              </template>
            </div>
          </v-tab-item>

          <!-- Sub-tab: Dados (data table) -->
          <v-tab-item>
            <v-data-table
              :headers="dadosHeaders"
              :items="dadosData"
              :items-per-page="dadosFilter.pageSize"
              class="main-table mt-4"
              hide-default-footer
              hide-default-header
              mobile-breakpoint="0"
              :loading="dadosLoading"
            >
              <template #header="{ props, on }">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      v-for="header in props.headers"
                      :key="header.value"
                    >
                      <div>
                        <v-hover v-slot="{ hover }">
                          <div>
                            <span>{{ $t(header.text) }}</span>
                            <v-icon
                              v-if="hover && header.sortable !== false"
                              color="white"
                              class="mx-2"
                              @click="on.sort(header.value)"
                            >
                              {{
                                props.options.sortBy.includes(header.value)
                                  ? 'mdi-chevron-down'
                                  : 'mdi-chevron-up'
                              }}
                            </v-icon>
                          </div>
                        </v-hover>
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>

              <template #[`item.readingDate`]="{ item }">
                <span>{{ formatDate(item.readingDate) }}</span>
              </template>
              <template #[`item.consumptionType.name`]="{ item }">
                <span>{{ item.consumptionType.name }}</span>
              </template>
              <template #[`item.consumptionType.unityName`]="{ item }">
                <span>{{ item.consumptionType.unityName }}</span>
              </template>
              <template #[`item.consumptionPoint.name`]="{ item }">
                <span>{{ item.consumptionPoint.name }}</span>
              </template>
              <template #[`item.value`]="{ item }">
                <span>{{ item.value }} {{ item.consumptionType.unityName }}</span>
              </template>
              <template #[`item.cost`]="{ item }">
                <span>{{ formatCurrency(item.unityValue * item.value) }}</span>
              </template>
            </v-data-table>

            <div class="pagination mx-2">
              <div class="item-pagination block-one">
                <div class="registros" v-if="dadosData.length">
                  <span>
                    1 - {{ dadosData.length }} de {{ dadosFilter.totalItems }}
                  </span>
                  <br />
                </div>
              </div>
              <div class="item-pagination block-two">
                <v-pagination
                  class="pagination-item"
                  v-model="dadosFilter.page"
                  :length="dadosTotalPages"
                  :total-visible="5"
                ></v-pagination>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-tab-item>

      <!-- ═══ Tab: Tipos de Consumo ═══ -->
      <v-tab-item>
        <div class="d-flex justify-end">
          <v-btn
            class="secondary-button mr-4"
            depressed
            to="consumption/manage"
          >
            {{ $t('Cadastrar tipo') }}
          </v-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="typeFilter.pageSize"
          class="main-table"
          hide-default-footer
          hide-default-header
          mobile-breakpoint="0"
        >
          <template #header="{ props, on }">
            <thead>
              <tr>
                <th
                  class="text-center"
                  v-for="header in props.headers"
                  :key="header.value"
                >
                  <v-simple-checkbox
                    v-if="header.value === 'data-table-select'"
                    v-model="props.everyItem"
                    :indeterminate="props.someItems && !props.everyItem"
                    color="purple"
                    @input="on['toggle-select-all']"
                  ></v-simple-checkbox>

                  <div>
                    <v-hover v-slot="{ hover }">
                      <div>
                        <span>{{ $t(header.text) }}</span>
                        <v-icon
                          v-if="
                            hover &&
                            header.value != 'data-table-select' &&
                            header.value != 'actions'
                          "
                          color="white"
                          class="mx-2"
                          @click="on.sort(header.value)"
                        >
                          {{
                            props.options.sortBy.includes(header.value)
                              ? `mdi-chevron-down`
                              : `mdi-chevron-up`
                          }}
                        </v-icon>
                      </div>
                    </v-hover>
                  </div>
                </th>
              </tr>
            </thead>
          </template>

          <template #[`item.name`]="{ item }">
            <span>
              {{ item.name }}
            </span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            <span> {{ formatDate(item.createdAt) }} </span>
          </template>
          <template #[`item.unityName`]="{ item }">
            <span> {{ item.unityName }} </span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  class="ma-1"
                  color="#0F3565"
                  dark
                  small
                  depressed
                  :to="`/consumption/manage?id=${item.id}`"
                  v-on="on"
                >
                  <v-icon size="22">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Editar') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  class="ma-1"
                  color="#921616"
                  depressed
                  small
                  dark
                  @click="openDelete(item)"
                  v-on="on"
                >
                  <v-icon size="22">mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Excluir') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <div class="pagination mx-2">
          <div class="item-pagination block-one">
            <v-menu bottom offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="btn"
                  depressed
                  color="#C0C0C0"
                  small
                >
                  qtd: {{ typeFilter.pageSize }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(size, i) in sizes"
                  :key="i"
                  @click="typeFilter.pageSize = size"
                >
                  <v-list-item-title>{{ size }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div class="registros" v-if="items.length">
              <span>
                1 - {{ items.length }} de {{ typeFilter.totalItems }}
              </span>
              <br />
            </div>
          </div>

          <div class="item-pagination block-two">
            <v-pagination
              class="pagination-item"
              v-model="typeFilter.page"
              :length="typeFilter.totalPages"
              :total-visible="5"
            ></v-pagination>
          </div>
        </div>
      </v-tab-item>

      <!-- ═══ Tab: Leituras ═══ -->
      <v-tab-item>
        <div class="d-flex justify-end">
          <v-btn class="secondary-button mr-4" depressed @click="addReading()">
            {{ $t('Cadastrar leitura') }}
          </v-btn>
        </div>

        <v-row class="ma-0" justify="end">
          <v-col cols="2">
            <span class="text ml-1">{{ $t('Tipo de consumo') }}</span>
            <v-autocomplete
              v-model="reportFilter.name"
              placeholder="Selecione"
              hide-details="auto"
              clearable
              append-icon="mdi-chevron-down"
              outlined
              :items="allTypes"
              item-text="name"
              item-value="name"
              @keyup.enter="getReport"
              class="primary-input mr-2"
            >
            </v-autocomplete>
          </v-col>

          <v-col cols="2">
            <span class="text ml-1">{{ $t('Ponto de consumo') }}</span>
            <v-autocomplete
              placeholder="Selecione"
              hide-details="auto"
              v-model="reportFilter.comsumptionPointId"
              clearable
              append-icon="mdi-chevron-down"
              outlined
              item-text="name"
              item-value="id"
              :items="allPoints"
              @keyup.enter="getReport"
              class="primary-input mr-2"
            >
            </v-autocomplete>
          </v-col>

          <v-col cols="2">
            <span class="text ml-1">{{ $t('Data inicial') }}</span>
            <v-menu
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  placeholder="00/00/0000"
                  :value="formatDate(reportFilter.start)"
                  hide-details="auto"
                  clearable
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  @keyup.enter="getReport"
                  autocomplete="off"
                  class="primary-input mr-2"
                ></v-text-field>
              </template>
              <v-date-picker locale="pt" v-model="reportFilter.start" no-title>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="2">
            <span class="text ml-1">{{ $t('Data final') }}</span>
            <v-menu
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="primary-input"
                  placeholder="00/00/0000"
                  :value="formatDate(reportFilter.end)"
                  :rules="
                    reportFilter.start
                      ? [
                          () =>
                            reportFilter.end >= reportFilter.start ||
                            'Data inválida',
                        ]
                      : []
                  "
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  readonly
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                  @keyup.enter="getReport"
                ></v-text-field>
              </template>
              <v-date-picker locale="pt" v-model="reportFilter.end" no-title>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="2" class="d-flex justify-center align-center">
            <v-btn class="primary-button mr-0" @click="getReport">
              Filtrar
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="reportHeaders"
          :items="reportData"
          :items-per-page="reportFilter.pageSize"
          class="main-table"
          hide-default-footer
          hide-default-header
          mobile-breakpoint="0"
        >
          <template #header="{ props, on }">
            <thead>
              <tr>
                <th
                  class="text-center"
                  v-for="header in props.headers"
                  :key="header.value"
                >
                  <v-simple-checkbox
                    v-if="header.value === 'data-table-select'"
                    v-model="props.everyItem"
                    :indeterminate="props.someItems && !props.everyItem"
                    color="purple"
                    @input="on['toggle-select-all']"
                  ></v-simple-checkbox>

                  <div>
                    <v-hover v-slot="{ hover }">
                      <div>
                        <span>{{ $t(header.text) }}</span>
                        <v-icon
                          v-if="
                            hover &&
                            header.value != 'data-table-select' &&
                            header.value != 'actions'
                          "
                          color="white"
                          class="mx-2"
                          @click="on.sort(header.value)"
                        >
                          {{
                            props.options.sortBy.includes(header.value)
                              ? `mdi-chevron-down`
                              : `mdi-chevron-up`
                          }}
                        </v-icon>
                      </div>
                    </v-hover>
                  </div>
                </th>
              </tr>
            </thead>
          </template>

          <template #[`item.consumptionType.name`]="{ item }">
            <span> {{ item.consumptionType.name }} </span>
          </template>
          <template #[`item.consumptionPoint.name`]="{ item }">
            <span> {{ item.consumptionPoint.name }} </span>
          </template>

          <template #[`item.value`]="{ item }">
            <span> {{ item.value }} {{ item.consumptionType.unityName }} </span>
          </template>
          <template #[`item.unityValue`]="{ item }">
            <span> {{ formatCurrency(item.unityValue) }} </span>
          </template>
          <template #[`item.finalValue`]="{ item }">
            <span> {{ formatCurrency(item.unityValue * item.value) }} </span>
          </template>
          <template #[`item.readingDate`]="{ item }">
            <span> {{ formatDate(item.readingDate) }} </span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  class="ma-1"
                  color="#0F3565"
                  dark
                  small
                  depressed
                  @click="editReading(item)"
                  v-on="on"
                >
                  <v-icon size="22">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Editar') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  class="ma-1"
                  color="#921616"
                  depressed
                  dark
                  small
                  @click="openDeleteReading(item)"
                  v-on="on"
                >
                  <v-icon size="22">mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Excluir') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <div class="pagination mx-2">
          <div class="item-pagination block-one">
            <div class="registros" v-if="reportData.length">
              <span>
                1 - {{ reportData.length }} de {{ reportFilter.totalItems }}
              </span>
              <br />
            </div>
          </div>

          <div class="item-pagination block-two">
            <v-pagination
              class="pagination-item"
              v-model="reportFilter.page"
              :length="totalPages"
              :total-visible="5"
            ></v-pagination>
          </div>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="deleteDialog" max-width="350">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Tem certeza de que deseja excluir o item') }}
            {{ currentItem.name }}?
          </div>

          <v-btn
            :disabled="loading"
            class="ma-1"
            color="grey"
            text
            @click="deleteDialog = false"
          >
            {{ $t('Cancelar') }}
          </v-btn>

          <v-btn
            :loading="loading"
            class="ma-1"
            color="error"
            text
            @click="remove"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>
    <v-dialog v-model="readingDeleteDialog" max-width="300">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Tem certeza de que deseja excluir a leitura?') }}
          </div>

          <v-btn
            :disabled="loading"
            class="ma-1"
            color="grey"
            text
            @click="readingDeleteDialog = false"
          >
            {{ $t('Cancelar') }}
          </v-btn>

          <v-btn
            :loading="loading"
            class="ma-1"
            color="error"
            text
            @click="removeReading"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>
    <v-dialog v-model="dialog" max-width="800px" persistent>
      <ManageReadings
        :title="$t('Adicionar Leitura')"
        @close="closeDialog"
        :key="dialogKey"
      />
    </v-dialog>
    <v-dialog v-model="editDialog" max-width="800px" persistent>
      <ManageReadings
        :title="$t('Editar Leitura')"
        :reading="currentReading"
        @close="closeDialog"
        :key="dialogKey"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import ApiService from '../../../services/ApiService'
import FormatDate from '../../../Utils/FormatDate'
import FormatCurrency from '../../../Utils/FormatCurrency'
import ManageReadings from './components/ManageReadings.vue'
import moment from 'moment'

import VChart, { THEME_KEY } from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components'
import { buildBarOption, buildLineOption, buildHorizontalBarOption } from '../Reports/components/GenerateReport/components/shared/chartConfigs'
import { palette, variedBarColors } from '../Reports/components/GenerateReport/components/shared/echartsTheme'

use([CanvasRenderer, BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent])

export default {
  name: 'ConsumptionPage',

  components: {
    ManageReadings,
    VChart,
  },

  provide: { [THEME_KEY]: 'light' },

  data() {
    return {
      allTypes: [],
      points: [],
      currentReading: {},
      editDialog: false,
      tab: 0,
      indicatorTab: 0,
      apiService: new ApiService(),
      show: false,
      loading: false,
      items: [],
      currentItem: [],
      unidades: [],
      unidLoading: false,
      sizes: [10, 30, 90, 180],
      totalPages: 1,
      totalItems: 0,
      typeFilter: {
        name: '',
        institutionId: null,
        page: 1,
        pageSize: 10,
      },
      reportFilter: {
        name: '',
        unity: null,
        institutionId: null,
        page: 1,
        pageSize: 10,
        start: '',
        end: '',
      },
      headers: [
        { text: '', align: 'center', value: '', sortable: false },
        { text: 'Tipo', align: 'center', value: 'name' },
        { text: 'Unidade', align: 'center', value: 'unityName' },
        { text: 'Data de criação', align: 'center', value: 'createdAt' },
        { text: '', align: 'center', value: 'actions', sortable: false },
      ],
      reportHeaders: [
        { text: '', align: 'center', value: '', sortable: false },
        { text: 'Tipo de consumo', align: 'center', value: 'consumptionType.name' },
        { text: 'Ponto de consumo', align: 'center', value: 'consumptionPoint.name' },
        { text: 'Valor de consumo', align: 'center', value: 'total' },
        { text: 'Preço por Unidade', align: 'center', value: 'unityValue' },
        { text: 'Valor', align: 'center', value: 'finalValue' },
        { text: 'Data de criação', align: 'center', value: 'readingDate' },
        { text: '', align: 'center', value: 'actions', sortable: false },
      ],
      dadosHeaders: [
        { text: '', align: 'center', value: '', sortable: false },
        { text: 'Data', align: 'center', value: 'readingDate' },
        { text: 'Tipo de consumo', align: 'center', value: 'consumptionType.name' },
        { text: 'Unidade', align: 'center', value: 'consumptionType.unityName' },
        { text: 'Ponto de leitura', align: 'center', value: 'consumptionPoint.name' },
        { text: 'Consumo', align: 'center', value: 'value' },
        { text: 'Custo', align: 'center', value: 'cost' },
      ],
      institution: {},
      deleteDialog: false,
      readingDeleteDialog: false,
      reportData: [],
      user: {},
      dialogKey: false,
      dialog: false,
      // Chart state
      chartFilter: {
        consumptionTypeId: null,
        consumptionPointId: null,
        unidadeId: null,
        startDate: null,
        endDate: null,
      },
      chartLoading: false,
      excelLoading: false,
      costByMonthData: null,
      costByPointData: null,
      consumptionByMonthData: null,
      consumptionByPointData: null,
      // Dados tab state
      dadosData: [],
      dadosLoading: false,
      dadosTotalPages: 1,
      dadosFilter: {
        page: 1,
        pageSize: 15,
        totalItems: 0,
      },
    }
  },

  created() {
    this.user = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))

    this.typeFilter.institutionId = this.institution.id
    this.reportFilter.institutionId = this.institution.id

    // Default: no date filter (show all months)
    this.chartFilter.startDate = null
    this.chartFilter.endDate = null

    Promise.all([this.list(), this.getTypes(), this.getReport(), this.getUnidades()])
    this.fetchIndicators()
    this.fetchDadosData()
  },

  computed: {
    allPoints() {
      if (this.reportFilter.name && this.reportFilter.name != 'null') {
        const type = this.allTypes.find((x) => x.name == this.reportFilter.name)
        return this.points.filter((x) => x.consumptionTypeId == type.id)
      }
      return this.points
    },

    chartPoints() {
      if (this.chartFilter.consumptionTypeId) {
        return this.points.filter((x) => x.consumptionTypeId == this.chartFilter.consumptionTypeId)
      }
      return this.points
    },

    hasChartData() {
      return (
        (this.costByMonthData && this.costByMonthData.data && this.costByMonthData.data.length > 0) ||
        (this.consumptionByMonthData && this.consumptionByMonthData.data && this.consumptionByMonthData.data.length > 0)
      )
    },

    kpiCards() {
      const cards = []
      if (this.costByMonthData) {
        cards.push({
          label: 'Custo Total',
          value: this.formatCurrency(this.costByMonthData.totalCost),
          color: '#bbcf20',
        })
      }
      if (this.consumptionByMonthData) {
        const unity = this.consumptionByMonthData.unity || ''
        cards.push({
          label: `Consumo Total${unity ? ' (' + unity + ')' : ''}`,
          value: this.formatNumber(this.consumptionByMonthData.totalConsumption),
          color: '#3C8EA6',
        })
      }
      if (this.costByPointData) {
        cards.push({
          label: 'Pontos de Leitura',
          value: String(this.costByPointData.data ? this.costByPointData.data.length : 0),
          color: '#fff',
        })
      }
      if (this.consumptionByMonthData) {
        cards.push({
          label: 'Meses Analisados',
          value: String(this.consumptionByMonthData.data ? this.consumptionByMonthData.data.length : 0),
          color: '#fff',
        })
      }
      return cards
    },

    costByMonthOption() {
      if (!this.costByMonthData || !this.costByMonthData.data || this.costByMonthData.data.length === 0) return {}
      const labels = this.costByMonthData.data.map((d) => d.label)
      const values = this.costByMonthData.data.map((d) => d.totalValue)
      return this.toCurrency(this.toDark(buildLineOption({
        categories: labels,
        series: [{ name: 'Custo (R$)', data: values, color: '#bbcf20', area: true }],
        showLegend: false,
      })))
    },

    consumptionByMonthOption() {
      if (!this.consumptionByMonthData || !this.consumptionByMonthData.data || this.consumptionByMonthData.data.length === 0) return {}
      const labels = this.consumptionByMonthData.data.map((d) => d.label)
      const values = this.consumptionByMonthData.data.map((d) => d.totalValue)
      const unity = this.consumptionByMonthData.unity || ''
      return this.toDark(buildLineOption({
        categories: labels,
        series: [{ name: `Consumo${unity ? ' (' + unity + ')' : ''}`, data: values, color: '#3C8EA6', area: true }],
        showLegend: false,
      }))
    },

    costByPointOption() {
      if (!this.costByPointData || !this.costByPointData.data || this.costByPointData.data.length === 0) return {}
      const labels = this.costByPointData.data.map((d) => d.consumptionPointName)
      const values = this.costByPointData.data.map((d) => d.totalValue)
      return this.toCurrency(this.toDark(buildHorizontalBarOption({
        categories: labels,
        series: [{ name: 'Custo (R$)', data: values, colors: variedBarColors }],
        showLegend: false,
      })))
    },

    consumptionByPointOption() {
      if (!this.consumptionByPointData || !this.consumptionByPointData.data || this.consumptionByPointData.data.length === 0) return {}
      const labels = this.consumptionByPointData.data.map((d) => d.consumptionPointName)
      const values = this.consumptionByPointData.data.map((d) => d.totalValue)
      return this.toDark(buildHorizontalBarOption({
        categories: labels,
        series: [{ name: 'Consumo', data: values, colors: variedBarColors }],
        showLegend: false,
      }))
    },
  },

  methods: {
    toCurrency(opt) {
      const formatter = (value) => FormatCurrency.brl(value)

      if (opt.yAxis && opt.yAxis.type === 'value') {
        opt.yAxis.axisLabel = { ...(opt.yAxis.axisLabel || {}), formatter }
      }
      if (opt.xAxis && opt.xAxis.type === 'value') {
        opt.xAxis.axisLabel = { ...(opt.xAxis.axisLabel || {}), formatter }
      }

      if (opt.tooltip) {
        opt.tooltip.valueFormatter = formatter
      }

      if (opt.series) {
        opt.series.forEach((serie) => {
          if (serie.label && serie.label.show) {
            serie.label.formatter = ({ value }) => (value > 0 ? formatter(value) : '')
          }
        })
      }

      return opt
    },

    toDark(opt) {
      const white = 'rgba(255,255,255,0.85)'
      const whiteSoft = 'rgba(255,255,255,0.55)'
      const gridLine = 'rgba(255,255,255,0.1)'

      opt.backgroundColor = 'transparent'

      if (opt.xAxis) {
        opt.xAxis.axisLabel = { ...(opt.xAxis.axisLabel || {}), color: whiteSoft }
        opt.xAxis.axisLine = { lineStyle: { color: 'rgba(255,255,255,0.2)' } }
        opt.xAxis.splitLine = { ...(opt.xAxis.splitLine || {}), lineStyle: { color: gridLine, type: 'dashed' } }
      }
      if (opt.yAxis) {
        opt.yAxis.axisLabel = { ...(opt.yAxis.axisLabel || {}), color: whiteSoft }
        opt.yAxis.axisLine = { lineStyle: { color: 'rgba(255,255,255,0.2)' } }
        opt.yAxis.splitLine = { ...(opt.yAxis.splitLine || {}), lineStyle: { color: gridLine, type: 'dashed' } }
      }

      if (opt.tooltip) {
        opt.tooltip.backgroundColor = 'rgba(15,53,101,0.95)'
        opt.tooltip.borderColor = 'rgba(255,255,255,0.15)'
        opt.tooltip.textStyle = { ...(opt.tooltip.textStyle || {}), color: '#fff' }
      }

      if (opt.legend) {
        opt.legend.textStyle = { ...(opt.legend.textStyle || {}), color: whiteSoft }
      }

      if (opt.series) {
        opt.series.forEach((s) => {
          if (s.label) {
            s.label.color = white
          }
        })
      }

      return opt
    },

    formatMonth(dateStr) {
      if (!dateStr) return '---'
      return moment(dateStr).format('MMM/YYYY').toUpperCase()
    },

    formatNumber(value) {
      return FormatCurrency.number(value)
    },

    onTypeChange() {
      this.chartFilter.consumptionPointId = null
    },

    getUnidades() {
      this.apiService
        .get(`unidade/listByUserAndInstitution/${this.institution.id}`)
        .then((response) => {
          this.unidades = response.content
        })
    },

    getTypes() {
      this.apiService
        .get(`ConsumptionType/list?institutionId=${this.institution.id}`)
        .then((response) => {
          this.allTypes = response.content
          this.points = this.allTypes
            .map(function (p) {
              return p.consumptionPoints
            })
            .reduce(function (a, b) {
              return a.concat(b)
            }, [])
        })
    },

    applyChartFilter() {
      this.fetchIndicators()
      this.dadosFilter.page = 1
      this.fetchDadosData()
    },

    buildChartPayload() {
      const startDate = this.chartFilter.startDate
        ? moment(this.chartFilter.startDate).startOf('month').format('YYYY-MM-DD')
        : null
      const endDate = this.chartFilter.endDate
        ? moment(this.chartFilter.endDate).endOf('month').format('YYYY-MM-DD')
        : null

      return {
        institutionId: this.institution.id,
        consumptionTypeId: this.chartFilter.consumptionTypeId,
        consumptionPointId: this.chartFilter.consumptionPointId,
        unidadeId: this.chartFilter.unidadeId,
        startDate,
        endDate,
      }
    },

    requestExcel() {
      this.excelLoading = true

      const now = new Date()
      const filename = `ControleDeConsumo_${now.getDate()}-${
        now.getMonth() + 1
      }-${now.getFullYear()}.xlsx`

      this.apiService
        .post('consumptionReading/dashboard/excel', this.buildChartPayload())
        .then((response) => {
          const link = document.createElement('a')
          link.href = `data:application/octet-stream;base64,${response.content}`
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    async fetchIndicators() {
      this.chartLoading = true
      try {
        const payload = this.buildChartPayload()

        const [costMonth, costPoint, consMonth, consPoint] = await Promise.all([
          this.apiService.post('consumptionReading/dashboard/cost-month-by-month', payload),
          this.apiService.post('consumptionReading/dashboard/cost-by-reading-point', payload),
          this.apiService.post('consumptionReading/dashboard/consumption-month-by-month', payload),
          this.apiService.post('consumptionReading/dashboard/consumption-by-reading-point', payload),
        ])

        this.costByMonthData = costMonth.content
        this.costByPointData = costPoint.content
        this.consumptionByMonthData = consMonth.content
        this.consumptionByPointData = consPoint.content
      } catch (e) {
        console.error('Error fetching indicators:', e)
      } finally {
        this.chartLoading = false
      }
    },

    fetchDadosData() {
      this.dadosLoading = true
      const typeName = this.chartFilter.consumptionTypeId
        ? (this.allTypes.find((t) => t.id === this.chartFilter.consumptionTypeId) || {}).name
        : null

      const startDate = this.chartFilter.startDate
        ? moment(this.chartFilter.startDate).startOf('month').format('YYYY-MM-DD')
        : null
      const endDate = this.chartFilter.endDate
        ? moment(this.chartFilter.endDate).endOf('month').format('YYYY-MM-DD')
        : null

      const model = {
        name: typeName,
        institutionId: this.institution.id,
        start: startDate,
        end: endDate,
        comsumptionPointId: this.chartFilter.consumptionPointId,
        page: this.dadosFilter.page,
        pageSize: this.dadosFilter.pageSize,
      }

      this.apiService
        .post('consumptionreading/paginate', model)
        .then((response) => {
          this.dadosData = response.content.data
          const pager = response.content.pager
          this.dadosFilter.page = pager.currentPage
          this.dadosFilter.pageSize = pager.pageSize
          this.dadosFilter.totalItems = pager.totalItems
          this.dadosTotalPages = pager.totalPages
        })
        .finally(() => {
          this.dadosLoading = false
        })
    },

    addReading() {
      this.dialogKey = !this.dialogKey
      this.dialog = true
    },

    editReading(item) {
      this.currentReading = item
      this.dialogKey = !this.dialogKey
      this.editDialog = true
    },
    closeDialog() {
      this.dialog = false
      this.editDialog = false
      this.aboutDialog = false

      this.reportReset()
    },

    formatCurrency(value) {
      return FormatCurrency.brl(value)
    },

    openDelete(item) {
      this.currentItem = item
      this.deleteKey = !this.deleteKey
      this.deleteDialog = true
    },
    openDeleteReading(item) {
      this.currentReading = item
      this.readingDeleteKey = !this.readingDeleteKey
      this.readingDeleteDialog = true
    },
    removeReading() {
      this.loading = true
      this.apiService
        .delete(`consumptionreading/${this.currentReading.id}`)
        .then(() => {
          this.getReport()
          this.$toast.success(this.$t('Leitura excluída com sucesso!'))
          this.loading = false
          this.readingDeleteDialog = false
        })
        .catch(() => {
          this.loading = false
          this.readingDeleteDialog = false
        })
    },

    remove() {
      this.loading = true
      this.apiService
        .delete(`consumptiontype/${this.currentItem.id}`)
        .then(() => {
          this.list()
          this.$toast.success(this.$t('Tipo excluído com sucesso!'))
          this.loading = false
          this.deleteDialog = false
        })
        .catch(() => {
          this.loading = false
          this.deleteDialog = false
        })
    },

    formatDate(date) {
      return FormatDate.dateTime(date)
    },

    reset() {
      this.typeFilter.page = 1

      this.typeFilter.name = ''
      this.list()
    },

    reportReset() {
      this.reportFilter.name = ''

      this.typeFilter.page = 1

      this.typeFilter.name = ''
      this.getReport()
    },

    getReport() {
      const model = {
        name: this.reportFilter.name,
        institutionId: this.reportFilter.institutionId,
        start: this.reportFilter.start,
        end: this.reportFilter.end,
        comsumptionPointId: this.reportFilter.comsumptionPointId,
        page: this.reportFilter.page,
        pageSize: this.reportFilter.pageSize,
      }
      if (this.reportFilter.unity !== null) {
        model.unity = this.reportFilter.unity
      }
      this.apiService
        .post(`consumptionreading/paginate`, model)
        .then((response) => {
          this.reportData = response.content.data
          const pager = response.content.pager
          this.reportFilter.page = pager.currentPage
          this.reportFilter.pageSize = pager.pageSize
          this.reportFilter.totalItems = pager.totalItems
          this.totalPages = pager.totalPages
        })
    },

    list() {
      this.apiService
        .get('consumptiontype/paginate', this.typeFilter)
        .then((response) => {
          const pager = response.content.pager

          this.typeFilter.totalItems = pager.totalItems
          this.items = response.content.data

          this.typeFilter.page = pager.currentPage
          this.typeFilter.pageSize = pager.pageSize

          this.typeFilter.totalPages = pager.totalPages
        })
    },
  },

  watch: {
    'typeFilter.pageSize': {
      deep: true,
      handler() {
        this.list()
      },
    },
    'typeFilter.page': {
      deep: true,
      handler() {
        this.list()
      },
    },
    'reportFilter.page': {
      deep: true,
      handler() {
        this.getReport()
      },
    },
    'dadosFilter.page': {
      handler() {
        this.fetchDadosData()
      },
    },
  },
}
</script>

<style scoped>
/* ── Filter bar ── */
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 8px;
}

.filter-col {
  flex: 1 1 160px;
  min-width: 160px;
}

.filter-col--period {
  flex: 2 1 320px;
}

.filter-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
  margin-left: 4px;
  white-space: nowrap;
}

.filter-input {
  height: 40px;
}

.filter-btn {
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  min-width: 90px;
  text-transform: none !important;
}

/* ── Dark panel ── */
.dark-panel {
  background: linear-gradient(135deg, #0f3565 0%, #1a4a7a 100%);
  border-radius: 12px;
  padding: 20px;
  min-height: 200px;
}

.kpi-card {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.kpi-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 4px;
}

.chart-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 16px;
}

.chart-title {
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.chart {
  width: 100%;
  height: 320px;
}

.chart--tall {
  height: 420px;
}
</style>
